import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useGoogleContacts } from "@/hooks/useGoogleContacts";
import GoogleContactsModal from "./GoogleContactsModal";
import AddContactManuallyModal from "./AddContactManuallyModal";
import { useToast } from "@/hooks/use-toast";
import { useCreateContact, useContactLimits } from "@/hooks/api/useContacts";
import type { GoogleContact } from "@/types/googleContacts";
import type { ContactCreateUpdateRequest } from "@/types/api";
import { Info } from "lucide-react";

interface Step2ContactImportProps {
  onBack: () => void;
  onContinue: () => void;
}

const Step2ContactImport = ({ onBack, onContinue }: Step2ContactImportProps) => {
  const [contactImportOption, setContactImportOption] = useState<string>("");
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const { contacts, isLoading, hasAccess, checkAccess, loadContacts } = useGoogleContacts();
  const { toast } = useToast();
  const createContact = useCreateContact();
  const { data: contactLimits, isLoading: isLoadingLimits } = useContactLimits();

  const handleContinue = async () => {
    // Check if user has reached contact limit
    if (contactLimits && !contactLimits.can_create) {
      toast({
        title: "Contact limit reached",
        description: contactLimits.message || "You have reached your contact limit.",
        variant: "destructive",
      });
      return;
    }

    if (contactImportOption === "google") {
      try {
        // Load contacts (this will handle authentication if needed)
        const loadedContacts = await loadContacts();
        
        if (loadedContacts.length > 0) {
          setShowContactsModal(true);
        } else {
          toast({
            title: "No contacts found",
            description: "Your Google account doesn't have any contacts to import.",
          });
        }
      } catch (error: any) {
        console.error("Error accessing Google contacts:", error);
        
        // Handle specific error cases
        if (error?.error === 'popup_closed_by_user') {
          toast({
            title: "Authentication cancelled",
            description: "Please click Continue again to import your Google contacts.",
          });
        } else if (error?.error === 'access_denied') {
          toast({
            title: "Access denied",
            description: "Please grant permission to access your contacts.",
          });
        }
        // For other errors, the hook already shows a toast
      }
    } else if (contactImportOption === "manual") {
      // For manual option, show the manual entry modal
      setShowManualModal(true);
    }
  };

  const handleImportContacts = async (selectedContacts: GoogleContact[]) => {
    try {
      // Check if the selected contacts exceed the remaining limit
      if (contactLimits && selectedContacts.length > contactLimits.remaining) {
        toast({
          title: "Contact limit exceeded",
          description: `You can only add ${contactLimits.remaining} more contact${contactLimits.remaining === 1 ? '' : 's'}. Please select fewer contacts.`,
          variant: "destructive",
        });
        return;
      }

      // Save each selected contact to the backend
      const promises = selectedContacts.map(googleContact => {
        // Extract name from the names array
        const name = googleContact.names?.[0]?.displayName || 
                    googleContact.names?.[0]?.givenName || 
                    googleContact.names?.[0]?.unstructuredName || 
                    'Unknown Contact';
        
        // Extract email from emailAddresses array
        const email = googleContact.emailAddresses?.[0]?.value;
        
        // Extract phone from phoneNumbers array
        const phone = googleContact.phoneNumbers?.[0]?.value;
        
        const contactData: ContactCreateUpdateRequest = {
          name: name,
          email: email || undefined,
          phone: phone || undefined,
          // We don't have country info from Google Contacts API
        };
        
        return createContact.mutateAsync(contactData);
      });

      await Promise.all(promises);
      
      setShowContactsModal(false);
      onContinue();
    } catch (error) {
      console.error("Error importing contacts:", error);
      toast({
        title: "Error importing contacts",
        description: "Some contacts could not be imported. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveManualContact = async (contactData: ContactCreateUpdateRequest) => {
    try {
      await createContact.mutateAsync(contactData);

      setShowManualModal(false);
      onContinue();
    } catch (error) {
      console.error("Error saving contact:", error);
      // Error toast is already handled by the mutation hook
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 bg-white">
      <div className="w-full max-w-[621px] shadow-md p-8 md:p-[56px] rounded-md mb-16">
        <div className="mb-8">
          <p className="text-[#6C4F77]/50 text-sm font-normal mb-2">
            Step 2 of 3 • Add contacts
          </p>
          <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
            Import your contacts
          </h1>
          <p className="text-[#666666] text-base text-[14px] md:text-[16px] font-normal">
            Import your contacts so Wabisabi can help you remember the people you care about
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <p className="text-[#666666] text-sm mb-4">Contact Synchronization</p>
            <RadioGroup
              value={contactImportOption}
              onValueChange={setContactImportOption}
              className="space-y-3"
            >
              <Label 
                htmlFor="google" 
                className="flex items-center space-x-3 border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
              >
                <RadioGroupItem value="google" id="google" />
                <span className="text-[14px] md:text-base font-normal text-gray-700 flex-1">
                  Google Contacts
                </span>
              </Label>
              
              <Label 
                htmlFor="manual" 
                className="flex items-center space-x-3 border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
              >
                <RadioGroupItem value="manual" id="manual" />
                <span className="text-[14px] md:text-base font-normal text-gray-700 flex-1">
                  Add contacts manually
                </span>
              </Label>
            </RadioGroup>
          </div>

          <div className="flex gap-3 !mt-[48px]">
            <Button
              variant="outline"
              onClick={onBack}
              className="flex-1 border-gray-200 text-gray-600 font-normal text-[14px] font-modernist py-[20px]"
            >
              Back
            </Button>
            
            <Button
              onClick={handleContinue}
              disabled={!contactImportOption || isLoading || isLoadingLimits || (contactLimits && !contactLimits.can_create)}
              className="flex-1 bg-[#6C4F77] hover:bg-[#5A3A68] disabled:bg-gray-300 text-white font-bold text-[14px] font-modernist py-[20px]"
            >
              {isLoading || isLoadingLimits ? "Loading..." : "Continue"}
            </Button>
          </div>
        </div>
      </div>

      <GoogleContactsModal
        isOpen={showContactsModal}
        onClose={() => setShowContactsModal(false)}
        contacts={contacts}
        isLoading={isLoading}
        onImport={handleImportContacts}
        maxSelectable={contactLimits?.remaining}
      />

      <AddContactManuallyModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSave={handleSaveManualContact}
      />
    </div>
  );
};

export default Step2ContactImport;